export default class DatabaseQuery {
    #rawQuery;
    #cleanedRawQuery = '';
    #wildcardTerms = [];
    #quotedTerms = [];
    #tokensInQuotedTerms = [];
    #simpleTokens = [];
    #srtTerms = [];
    #errors = [];
    #expansions = new Map();  // term -> expanded[]

    constructor(rawQuery = '') {
        this.#rawQuery = rawQuery;
        this.#cleanedRawQuery = rawQuery;
    }
    
    cleanRawQuery(makeKindAssumptions = true) {
        this.#cleanedRawQuery = this.#cleanedRawQuery
            .toLowerCase()
            .trim()
            // Replace multiple spaces with a single space
            .replace(/\s+/g, ' ')
            // Replace curly quotes and apostrophes (UTF-8 dudes) with stick versions (ASCII dudes)
            .replace(/(“|”)/g, '"')
            .replace(/’/g, "'")
            // Replace multiple plus symbols with a single plus symbol
            .replace(/\++/g, '+');
        
        const quoteCount = this.#cleanedRawQuery.match(/"/g)?.length || 0;
        if (quoteCount > 0 && quoteCount % 2 !== 0) {
            // But let's try to be kind. What if the user meant to end the query with a quote?
            if (makeKindAssumptions) {
                this.#cleanedRawQuery += '"';
            } else {
                this.#errors.push('Odd number of quotes in the search query');
            }
        }

        return this;
    }
    
    removeIllegalCharacters() {
        // Remove any characters that are not legit search chars
        this.#cleanedRawQuery = this.#cleanedRawQuery.replace(/[^-\s"'\[\]:.a-z0-9+]/g, '');
        return this;
    }

    buildListsOfTerms(makeKindAssumptions = true) {
        // Special case: if there's a colon in the query and no spaces, this is a pure SRT search.
        if (this.#cleanedRawQuery.includes(':') && !this.#cleanedRawQuery.includes(' ')) {
            if (!this.#cleanedRawQuery.startsWith('[')) {
                this.#cleanedRawQuery = '[' + this.#cleanedRawQuery;
            }
            if (!this.#cleanedRawQuery.endsWith(']')) {
                this.#cleanedRawQuery += ']';
            }
        }

        // Split into unique terms, honor quotes that group terms
        const discreteTerms = this.#cleanedRawQuery.match(/(?:("[^"]*?")|(\[.*?\])|\S+)/g) || [];

        discreteTerms.forEach(term => {
            if (term.startsWith('"') && term.endsWith('"')) {
                this.#quotedTerms.push(term.slice(1, -1));

            } else if (term.includes('+')) {
                this.#wildcardTerms.push(term);

            } else if (term.startsWith('[')) {
                const srtTerm = term.slice(1);
                if (srtTerm.endsWith(']')) {
                    this.#srtTerms.push(srtTerm.slice(0, -1));
                } else if (makeKindAssumptions) {
                    // We're kind and let people leave off the end bracket as a shorthand.
                    this.#srtTerms.push(srtTerm);
                } else {
                    this.#errors.push('SRT term not formatted correctly.');
                }

            } else {
                this.#simpleTokens.push(term);
            }
        });

        // Build a list of tokens contained within quoted terms
        this.#tokensInQuotedTerms = this.#quotedTerms.flatMap(term => term.split(' '));

        return this;
    }

    validate() {
        if (this.#quotedTerms.some(term => term.includes('+'))) {
            this.#errors.push(`Quoted term cannot contain a '+' wildcard`);
        }

        console.log("Here we are!");
        if (this.#wildcardTerms.some(term => {
            const plusCount = (term.match(/\+/g) || []).length;
            console.log(term, plusCount);
            if (plusCount === 0) return false; // No + symbols
            if (plusCount === 1 && term.startsWith('+')) return false; // + at start
            if (plusCount === 1 && term.endsWith('+')) return false; // + at end
            if (plusCount === 2 && term.startsWith('+') && term.endsWith('+')) return false; // + at start and end

            return true; // If we get here, there are too many + symbols in the wrong place.
        })) {
            this.#errors.push(`Wildcard symbols (+) can not be used in the middle of a search term, only at the start and/or end.`);
        }
        
        if (this.#srtTerms.length > 1) {
            this.#errors.push('Only one SRT term is allowed');
        }
        
        return this;
    }

    get isValid() {
        return this.#errors.length === 0;
    }
    
    /**
     * Add an expansion to the query.
     * @param {string} term - The term to expand
     * @param {string[]} expanded - The expanded terms
     * @returns {DatabaseQuery} The query object
     */
    addExpansion(term, expanded) {
        const existingExpansions = this.#expansions.get(term) || [];
        this.#expansions.set(term, [...new Set([...existingExpansions, ...expanded])]);
        return this;
    }

    /**
     * Compile the query into a format that can be used by the search service.
     * @returns {Object} The compiled query
     */
    compile() {
        return {
            cleanedRawQuery: this.#cleanedRawQuery,
            simpleTokens: [...this.#simpleTokens],
            expandedSimpleTokens: this.#simpleTokens.map(term => 
                this.#expansions.has(term) 
                    ? this.#expansions.get(term) 
                    : [term]
            ),
            wildcardTerms: [...this.#wildcardTerms],
            expandedWildcardTokens: this.#wildcardTerms
                .map(term => this.#expansions.has(term) ? this.#expansions.get(term) : undefined)
                .filter(result => result !== undefined),
            quotedTerms: [...this.#quotedTerms],
            tokensInQuotedTerms: [...this.#tokensInQuotedTerms],
            srtTerms: [...this.#srtTerms],
            isValid: this.isValid,
            errors: [...this.#errors],
            expansionMap: Object.fromEntries(this.#expansions)
        };
    }

    /**
     * Apply an expander function to this query
     * @param {Function} expander - Function that takes this query and returns it
     * @returns {DatabaseQuery} - this instance for chaining
     */
    expand(expander) {
        expander(this);
        return this;
    }

}
